import { Group, Slider, Stack } from '@mantine/core';
import { ReactFlowProvider } from '@xyflow/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProcessMapping } from '../../../api/process-client/process-client.hooks';
import { useGetWorkflows } from '../../../api/workflows-client/workflows.client.hooks';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { StatusLabelsProvider, useStatusLabelResolver } from '../../../contexts/status-labels.context';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { baseWhite, secondaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import type { EventScope } from '../context/process.context.type';
import { getScopedSubprojectIds } from '../process.helpers';
import type { ViewType } from '../process.type';
import { useEventGraph } from './event-graph-client.hooks';
import { SectionEventGraph } from './section-event-graph';

type Props = {
  viewType: ViewType;
  currentEventScope: EventScope;
  setCurrentEventScope: (scope: EventScope) => void;
  isRightGraph: boolean;
};

/**
 * Event graph for a single board: the task-event transitions that occurred
 * within the statuses the user drilled into from the process map.
 *
 * Provides source-cased status labels scoped to this graph's board, used both
 * for the scope chips in the header and — via the provider — for the event
 * nodes rendered inside React Flow. The statuses sent to the API keep their
 * stored lowercase form.
 *
 * @param {ViewType} viewType - Count, percentage, cycle time, or cost view
 * @param {EventScope} currentEventScope - Statuses and selected edge for this side
 * @param {(scope: EventScope) => void} setCurrentEventScope - Updates the scope, e.g. to navigate back
 * @param {boolean} isRightGraph - Whether this is the right side of the comparison view
 * @returns {JSX.Element} The event graph with its header controls
 */
export const EventGraph = ({ viewType, currentEventScope, setCurrentEventScope, isRightGraph }: Props) => {
  const i18n = useTranslation();
  const organizationId = useGlobalStore((state) => state.organization)?.id || '';
  const { scope: leftScope, rightScope } = useProcessStore();
  // Use the appropriate event scope based on the comparison view and side
  const scope = isRightGraph ? rightScope : leftScope;
  const { team, board, taskTypes, epic, workflow, startDate, endDate, useWorkflow, useGroups, task, workPeriod } =
    scope;

  const { data: workflows } = useGetWorkflows(organizationId);

  const paramWorkflow = useWorkflow
    ? workflow
    : board && !useGroups
      ? (workflows?.find((w) => w.subprojects.includes(board?.id)) ?? null)
      : null;

  // workflow-scoped mapping carries `labels` (per-workflow
  // bucket-label overrides). Hits the same React Query cache as the
  // upstream process page, so this is a cache read in steady state.
  const { mapping, query: mappingQuery } = useProcessMapping({
    workflowId: paramWorkflow?.id,
    teamId: team?.id,
    options: {
      enabled: Boolean(paramWorkflow?.id || team?.id),
    },
  });

  const { eventGraph, query } = useEventGraph({
    teamId: useWorkflow ? null : paramWorkflow !== null ? null : team?.id || '',
    boardId: board?.id || '',
    workPeriodId: workPeriod?.id || '',
    taskTypes: taskTypes?.length ? taskTypes.join(',') : '',
    epicId: epic?.id || '',
    workflowId: paramWorkflow?.id || '',
    startDate: startDate || '',
    endDate: endDate || '',
    statuses: currentEventScope.statuses,
    taskId: task?.id || '',
  });

  // Derived from the whole scope rather than `scope.board`, which is null
  // whenever the page is scoped by workflow or by team. The resolver labels the
  // scope chips rendered below; the provider carries the same labels down to
  // the event nodes, which sit too deep inside ReactFlow to receive a prop.
  // Both read the same React Query keys, so this is one set of requests.
  const scopedSubprojectIds = useMemo(() => getScopedSubprojectIds(scope), [scope]);
  const { getStatusLabel: resolveStatusLabelForDisplay } = useStatusLabelResolver(scopedSubprojectIds);

  const [filterLevel, setFilterLevel] = useState(1);

  const handleSliderChange = (value: number) => {
    setFilterLevel(value);
  };

  // Convert slider value (1-5) to a percentage (0.2-1.0)
  const filterPercentage = filterLevel * 0.2;

  const handleBack = () => {
    setCurrentEventScope({
      showEventGraph: false,
      statusGroup: null,
      statuses: [],
      selectedEdgeId: null,
    });
  };

  return (
    <StatusLabelsProvider subprojectIds={scopedSubprojectIds}>
      <BrandedLoadingOverlay variant="colored" visible={query.isLoading || mappingQuery.isLoading} />
      <Group justify="space-between">
        <Stack gap={1}>
          <Group gap="xs">
            <Button variant="secondary" size="xs" radius="xl" onClick={handleBack}>
              <Icon name="arrow_back" color={baseWhite} size={20} />
            </Button>
            <Text size="large" weight="bold">
              {currentEventScope.statusGroup}
            </Text>
          </Group>
          <Group gap="xs">
            <Text size="regular" weight="medium">
              {`${i18n.t('common.status.statuses')}:`}
            </Text>
            {currentEventScope.statuses.map((status, index) => (
              <Group key={index} gap="xs" align="center">
                <Text size="regular">{`${resolveStatusLabelForDisplay(status)} ${
                  index < currentEventScope.statuses.length - 1 ? ',' : ''
                }`}</Text>
              </Group>
            ))}
          </Group>
        </Stack>
        <Group>
          <Text>Filter Level</Text>
          <Slider
            min={1}
            max={5}
            step={1}
            value={filterLevel}
            onChange={handleSliderChange}
            label={null}
            marks={[
              { value: 1, label: '1' },
              { value: 2, label: '2' },
              { value: 3, label: '3' },
              { value: 4, label: '4' },
              { value: 5, label: '5' },
            ]}
            styles={{
              root: { width: '300px' },
              mark: { width: '4px', height: '4px' },
            }}
            color={secondaryBase}
          />
          <Tooltip
            variant="black"
            label="Lower values show only the most important connections between events"
            withArrow
          >
            <Icon name="info" />
          </Tooltip>
        </Group>
      </Group>
      <ReactFlowProvider>
        <div style={{ height: 'calc(100% - 45px)' }}>
          <SectionEventGraph
            eventGraph={eventGraph}
            filterLevel={filterPercentage}
            viewType={viewType}
            eventScope={currentEventScope}
            isRightGraph={isRightGraph}
            resolvedLabels={mapping?.labels}
          />
        </div>
      </ReactFlowProvider>
    </StatusLabelsProvider>
  );
};
